import React, { useEffect, useRef } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type {
  Control,
  UseFormGetValues,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import {
  FormSelect,
  VirtualizedFormSelect,
  Alert,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  ChevronDown,
} from 'lucide-react';
import type {
  KinnserClinicDataResponse,
  KinnserCreatePatientResponse,
} from '../../services/api/types.gen';
import { DateField } from '../../components/FormFields';
import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';

/** Intake shape needed for Kinnser auto-add APIs. */
interface SelectedIntakeForAutoAdd {
  id?: string;
  kinnser_matched_patient_key?: string | null;
  kinnser_clinic_id?: string | null;
  agency_clinic?: { clinic_key?: string } | null;
  patient_notes?: string | null;
  kinnser_soc_date?: string | null;
  kinnser_episode_start_date?: string | null;
}

interface KinnserSchedulingStepFormContentProps {
  // Form controls
  control: Control<SchedulingStepFormData>;
  register: UseFormRegister<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  setValue: UseFormSetValue<SchedulingStepFormData>;
  getValues: UseFormGetValues<SchedulingStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;

  // Save status
  saveToServer: (data: Partial<SchedulingStepFormData>) => void;

  // Clinics
  clinicData: KinnserClinicDataResponse | null;
  loadingClinicData: boolean;

  // Kinnser creation
  createResult: KinnserCreatePatientResponse | null;

  // Episode creation flag for conditional validation
  kinnserCreateEpisode?: boolean;

  // Whether this intake has been confirmed as an existing patient
  isExistingPatient?: boolean;

  // For existing patient: call auto-add episode / admission+episode APIs
  selectedIntake?: SelectedIntakeForAutoAdd | null;
  kinnserCreationType?:
    | 'new_admission'
    | 'new_episode'
    | 'current_episode'
    | null;
  /** Min date for episode start (e.g. day after prior episode start); YYYY-MM-DD. */
  minEpisodeStartDate?: string | null;
  /** Min date for SOC (e.g. day after prior discharge/start of care); YYYY-MM-DD. */
  minSocDate?: string | null;
  /** When true (new episode + matched episode not discharged), SOC date is locked to matched episode's start date. */
  socDateLockedToMatchedEpisode?: boolean;
  /** When socDateLockedToMatchedEpisode is true, this is the date to use and display (YYYY-MM-DD). */
  lockedSocDateStr?: string | null;
  /** When true (e.g. new episode with matched end date), episode start is locked to day after matched episode end. */
  episodeStartLockedToMatchedEnd?: boolean;
  /** When episodeStartLockedToMatchedEnd is true, this is the episode start date to use (YYYY-MM-DD). */
  lockedEpisodeStartDateStr?: string | null;
  /** When true (SOC + new admission + existing patient), episode start is locked to the Start of Care date. */
  episodeStartLockedToSoc?: boolean;
}

export const KinnserSchedulingStepFormContent: React.FC<
  KinnserSchedulingStepFormContentProps
> = ({
  control,
  register,
  errors,
  setValue,
  getValues: _getValues,
  handleFieldBlur,
  saveToServer,
  clinicData,
  loadingClinicData,
  createResult,
  isExistingPatient = false,
  selectedIntake,
  kinnserCreationType: _kinnserCreationType,
  minEpisodeStartDate,
  minSocDate,
  socDateLockedToMatchedEpisode = false,
  lockedSocDateStr = null,
  episodeStartLockedToMatchedEnd = false,
  lockedEpisodeStartDateStr = null,
  episodeStartLockedToSoc = false,
}) => {
  const saveToServerRef = useRef(saveToServer);
  saveToServerRef.current = saveToServer;

  const episodeStartMinDate =
    minEpisodeStartDate != null && minEpisodeStartDate !== ''
      ? (() => {
          const [y, m, d] = minEpisodeStartDate.split('-').map(Number);
          return new Date(y, m - 1, d);
        })()
      : undefined;

  const socMinDate =
    minSocDate != null && minSocDate !== ''
      ? (() => {
          const [y, m, d] = minSocDate.split('-').map(Number);
          return new Date(y, m - 1, d);
        })()
      : undefined;
  // Watch episode start date to check if it's empty when SOC date changes
  const episodeStartDate = useWatch({
    control,
    name: 'kinnser_episode_start_date',
  });
  const watchedSocDate = useWatch({
    control,
    name: 'kinnser_soc_date',
  });

  // Normalize date to YYYY-MM-DD for comparison (avoid redundant saves when intake already has value).
  const intakeSocDateOnly =
    selectedIntake?.kinnser_soc_date != null
      ? String(selectedIntake.kinnser_soc_date).split('T')[0]
      : null;
  const intakeEpisodeStartOnly =
    selectedIntake?.kinnser_episode_start_date != null
      ? String(selectedIntake.kinnser_episode_start_date).split('T')[0]
      : null;

  // When SOC is locked to matched episode (new episode + not discharged), keep form and intake in sync.
  // Only persist when locked and intake doesn't already have this value to avoid redundant requests.
  useEffect(() => {
    if (!socDateLockedToMatchedEpisode || !lockedSocDateStr) return;
    setValue('kinnser_soc_date', lockedSocDateStr);
    if (intakeSocDateOnly !== lockedSocDateStr) {
      void saveToServerRef.current({ kinnser_soc_date: lockedSocDateStr });
    }
  }, [
    socDateLockedToMatchedEpisode,
    lockedSocDateStr,
    setValue,
    intakeSocDateOnly,
  ]);

  // When episode start is locked to day after matched end, keep form and intake in sync.
  // Only persist when intake doesn't already have this value to avoid redundant requests.
  useEffect(() => {
    if (!episodeStartLockedToMatchedEnd || !lockedEpisodeStartDateStr) return;
    setValue('kinnser_episode_start_date', lockedEpisodeStartDateStr);
    if (intakeEpisodeStartOnly !== lockedEpisodeStartDateStr) {
      void saveToServerRef.current({
        kinnser_episode_start_date: lockedEpisodeStartDateStr,
      });
    }
  }, [
    episodeStartLockedToMatchedEnd,
    lockedEpisodeStartDateStr,
    setValue,
    intakeEpisodeStartOnly,
  ]);

  // SOC + new admission (existing patient): episode start must match Start of Care.
  useEffect(() => {
    if (!episodeStartLockedToSoc) return;
    const socNorm =
      watchedSocDate && typeof watchedSocDate === 'string'
        ? watchedSocDate.split('T')[0]
        : null;
    if (!socNorm) return;
    setValue('kinnser_episode_start_date', socNorm);
    if (intakeEpisodeStartOnly !== socNorm) {
      void saveToServerRef.current({
        kinnser_episode_start_date: socNorm,
      });
    }
  }, [
    episodeStartLockedToSoc,
    watchedSocDate,
    setValue,
    intakeEpisodeStartOnly,
  ]);

  return (
    <div className='flex flex-col'>
      {/* Validation errors */}
      {!isExistingPatient && Object.keys(errors).length > 0 && (
        <Alert className='mb-3 py-2 px-3 bg-red-50 border-red-100 text-red-900 text-xs'>
          <div className='flex items-start gap-2'>
            <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-red-600' />
            <div className='flex-1'>
              <p className='font-medium mb-1'>
                Missing fields required to schedule:
              </p>
              <ul
                className='flex flex-wrap gap-1'
                role='list'
                aria-label='Validation errors'
              >
                {Object.entries(errors)
                  .map(([field, error]) => {
                    const message =
                      (error as { message?: string })?.message || '';
                    const cleanMessage = message.replace(/ is required$/, '');

                    return { field, cleanMessage };
                  })
                  .filter(({ cleanMessage }) => cleanMessage !== '')
                  .map(({ field, cleanMessage }, i, arr) => (
                    <li key={field} role='listitem' className='text-xs'>
                      {cleanMessage}
                      {i < arr.length - 1 ? ',' : ''}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </Alert>
      )}
      {/* Validation errors or success message */}
      {createResult && (
        <div className='mb-3'>
          {createResult.success ? (
            <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
              <div className='flex items-center gap-2 text-green-800'>
                <CheckCircle className='h-4 w-4' />
                <span className='font-medium'>
                  Patient created successfully in Kinnser
                </span>
              </div>
              {createResult.kinnser_patient_id && (
                <div className='text-sm text-green-700 mt-1'>
                  Kinnser Patient ID: {createResult.kinnser_patient_id}
                </div>
              )}
            </div>
          ) : (
            <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
              <div className='flex items-center gap-2 text-red-800 mb-2'>
                <XCircle className='h-4 w-4' />
                <span className='font-medium'>Validation Errors</span>
              </div>
              <ul className='list-disc list-inside text-sm text-red-700 space-y-1'>
                {createResult.errors?.map((error, index) => (
                  <li key={index}>{error.message}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <form className='space-y-5' onSubmit={e => e.preventDefault()}>
        {/* Schedule Start of Care */}
        <div className='grid grid-cols-4 gap-3'>
          {/* Row 1 & 2: Assignment fields (hidden for existing patients) */}
          {!isExistingPatient && (
            <>
              <VirtualizedFormSelect
                label='Clinician'
                placeholder='Select clinician...'
                options={clinicData?.clinicians || []}
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_clinician_id?.message}
                {...register('kinnser_clinician_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='Clinical Care Manager'
                placeholder='Select clinical care manager...'
                options={clinicData?.clinical_care_managers || []}
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_clinical_care_manager_id?.message}
                {...register('kinnser_clinical_care_manager_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='Case Manager'
                placeholder='Select case manager...'
                options={clinicData?.case_managers || []}
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_case_manager_id?.message}
                {...register('kinnser_case_manager_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='OASIS Approver'
                placeholder='Select OASIS approver...'
                options={clinicData?.oasis_approvers || []}
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_oasis_approver_id?.message}
                {...register('kinnser_oasis_approver_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              {/* Row 2: PT, OT, ST Approvers */}
              <VirtualizedFormSelect
                label='PT Approver'
                placeholder='Select PT approver...'
                options={clinicData?.pt_approvers || []}
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_pt_approver_id?.message}
                {...register('kinnser_pt_approver_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='OT Approver'
                placeholder='Select OT approver...'
                options={clinicData?.ot_approvers || []}
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_ot_approver_id?.message}
                {...register('kinnser_ot_approver_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='ST Approver'
                placeholder='Select ST approver...'
                options={clinicData?.st_approvers || []}
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_st_approver_id?.message}
                {...register('kinnser_st_approver_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              {clinicData?.primary_schedulers &&
                clinicData.primary_schedulers.length > 0 && (
                  <VirtualizedFormSelect
                    label='Primary Scheduler'
                    placeholder='Select primary scheduler...'
                    options={clinicData.primary_schedulers}
                    required={true}
                    disabled={loadingClinicData}
                    error={errors.kinnser_primary_scheduler_id?.message}
                    {...register('kinnser_primary_scheduler_id', {
                      onBlur: handleFieldBlur,
                    })}
                  />
                )}

              {clinicData?.secondary_schedulers &&
                clinicData.secondary_schedulers.length > 0 && (
                  <VirtualizedFormSelect
                    label='Secondary Scheduler'
                    placeholder='Select secondary scheduler...'
                    options={clinicData.secondary_schedulers}
                    required={true}
                    disabled={loadingClinicData}
                    error={errors.kinnser_secondary_scheduler_id?.message}
                    {...register('kinnser_secondary_scheduler_id', {
                      onBlur: handleFieldBlur,
                    })}
                  />
                )}
            </>
          )}

          {/* Row 3: Type of SOC — editable for new patients; read-only for existing (Kinnser assigns in EMR) */}
          {isExistingPatient ? (
            <div className='flex flex-col gap-1'>
              <div className='flex items-center gap-1.5'>
                <span className='text-xs font-medium text-gray-700'>
                  Type of SOC
                </span>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type='button'
                        className='inline-flex text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-400 rounded'
                        aria-label='Type of SOC is assigned in Kinnser'
                      >
                        <Info className='h-3.5 w-3.5' />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side='top' className='max-w-xs text-xs'>
                      Automatically assigned in Kinnser
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {/* Hidden field keeps RHF/intake in sync; visible shell omits SN/PT so we do not
                  imply a value that may differ from what Kinnser assigns. */}
              <input
                type='hidden'
                {...register('kinnser_type_of_soc', {
                  onBlur: handleFieldBlur,
                })}
              />
              <div
                className='flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-muted-foreground opacity-60 cursor-not-allowed'
                aria-label='Type of SOC is automatically assigned in Kinnser'
              >
                <span className='min-w-0 flex-1' aria-hidden />
                <ChevronDown
                  className='h-4 w-4 shrink-0 opacity-50'
                  aria-hidden
                />
              </div>
              {errors.kinnser_type_of_soc?.message ? (
                <p className='text-xs text-red-600'>
                  {errors.kinnser_type_of_soc.message}
                </p>
              ) : null}
            </div>
          ) : (
            <FormSelect
              label='Type of SOC'
              placeholder='Select SOC type...'
              options={[
                { value: 'SN', label: 'Nursing' },
                { value: 'PT', label: 'Therapy' },
              ]}
              required={true}
              disabled={loadingClinicData}
              error={errors.kinnser_type_of_soc?.message}
              {...register('kinnser_type_of_soc', {
                onBlur: handleFieldBlur,
              })}
            />
          )}

          {isExistingPatient && (
            <VirtualizedFormSelect
              label='Clinician'
              placeholder='Select clinician...'
              options={clinicData?.clinicians || []}
              required={true}
              disabled={loadingClinicData}
              error={errors.kinnser_clinician_id?.message}
              {...register('kinnser_clinician_id', {
                onBlur: handleFieldBlur,
              })}
            />
          )}

          <div className='flex flex-col gap-0.5'>
            <Controller
              name='kinnser_soc_date'
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <DateField
                  label='Start of Care Date'
                  value={
                    socDateLockedToMatchedEpisode
                      ? (lockedSocDateStr ?? value)
                      : value
                  }
                  htmlFor='socDate'
                  required={true}
                  disabled={socDateLockedToMatchedEpisode}
                  error={errors.kinnser_soc_date?.message}
                  onChange={val => {
                    if (socDateLockedToMatchedEpisode) return;
                    onChange(val);
                    const toSave: Partial<SchedulingStepFormData> = {
                      kinnser_soc_date: val,
                    };
                    // Copy to episode start date if it's empty (matches Kinnser behavior),
                    // or when episode is locked to SOC (new admission + existing patient).
                    const norm =
                      val && typeof val === 'string' ? val.split('T')[0] : null;
                    if (
                      norm &&
                      (episodeStartLockedToSoc || (!episodeStartDate && val))
                    ) {
                      setValue('kinnser_episode_start_date', val);
                      toSave.kinnser_episode_start_date = val;
                    }
                    saveToServer(toSave);
                  }}
                  onBlur={onBlur}
                  minDate={socMinDate}
                  yearRange={{
                    fromYear: new Date().getFullYear() - 1,
                    toYear: new Date().getFullYear() + 1,
                  }}
                />
              )}
            />
            {socDateLockedToMatchedEpisode && (
              <p className='text-xs text-gray-500'>
                Same as matched episode&apos;s start of care (episode not
                discharged).
              </p>
            )}
          </div>

          <div className='flex flex-col gap-0.5'>
            <Controller
              name='kinnser_episode_start_date'
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <DateField
                  label='Episode Start Date'
                  value={
                    episodeStartLockedToMatchedEnd
                      ? (lockedEpisodeStartDateStr ?? value)
                      : episodeStartLockedToSoc
                        ? (() => {
                            const s =
                              watchedSocDate &&
                              typeof watchedSocDate === 'string'
                                ? watchedSocDate.split('T')[0]
                                : null;
                            return s ?? value;
                          })()
                        : value
                  }
                  htmlFor='episodeStartDate'
                  required={true}
                  disabled={
                    episodeStartLockedToMatchedEnd || episodeStartLockedToSoc
                  }
                  error={errors.kinnser_episode_start_date?.message}
                  onChange={val => {
                    if (
                      episodeStartLockedToMatchedEnd ||
                      episodeStartLockedToSoc
                    )
                      return;
                    onChange(val);
                    saveToServer({
                      kinnser_episode_start_date: val,
                    });
                  }}
                  onBlur={onBlur}
                  minDate={episodeStartMinDate}
                  yearRange={{
                    fromYear: new Date().getFullYear() - 1,
                    toYear: new Date().getFullYear() + 1,
                  }}
                />
              )}
            />
            {episodeStartLockedToMatchedEnd && (
              <p className='text-xs text-gray-500'>
                One day after matched episode&apos;s end date.
              </p>
            )}
            {episodeStartLockedToSoc && (
              <p className='text-xs text-gray-500'>
                Same as Start of Care date.
              </p>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};
